import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  EntrySettingsBody,
  Flow,
  FlowCreateBody,
  FlowDocument,
  FlowSaveBody,
  PreviewBody,
} from '../domain/types';
import { flowsApi, type ResultsParams } from '../infrastructure/api';

export const flowKeys = {
  all: ['flows'] as const,
  list: () => [...flowKeys.all, 'list'] as const,
  one: (id: string) => [...flowKeys.all, 'one', id] as const,
  settings: () => [...flowKeys.all, 'settings'] as const,
  results: (id: string) => [...flowKeys.all, 'results', id] as const,
  resultsPage: (id: string, params: ResultsParams) => [...flowKeys.results(id), params] as const,
  lessons: (q: string) => [...flowKeys.all, 'lessons', q] as const,
};

export const flowsQuery = queryOptions({ queryKey: flowKeys.list(), queryFn: flowsApi.list });
export const flowQuery = (id: string) =>
  queryOptions({ queryKey: flowKeys.one(id), queryFn: () => flowsApi.get(id) });
export const entrySettingsQuery = queryOptions({ queryKey: flowKeys.settings(), queryFn: flowsApi.settings });

export const useFlows = () => useQuery(flowsQuery);
// O builder nasce do primeiro carregamento e segue com o rascunho local:
// recarregar o fluxo por trás (foco, intervalo) não pode atropelar a edição.
export const useFlow = (id: string) => useQuery({ ...flowQuery(id), staleTime: Infinity });
export const useEntrySettings = () => useQuery(entrySettingsQuery);

export const useFlowResults = (id: string, params: ResultsParams) =>
  useQuery({
    queryKey: flowKeys.resultsPage(id, params),
    queryFn: () => flowsApi.results(id, params),
    placeholderData: keepPreviousData,
  });

/** Aulas para o bloco "Abrir aula/página" — a API pede ao menos 2 letras. */
export const useLessonSearch = (q: string) =>
  useQuery({
    queryKey: flowKeys.lessons(q),
    queryFn: () => flowsApi.lessons(q),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
  });

/** Escritas que devolvem o fluxo inteiro: cache do fluxo atualizado, lista recarregada. */
function useFlowWrite<V>(fn: (vars: V) => Promise<Flow>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (flow) => {
      qc.setQueryData(flowKeys.one(flow.id), flow);
      void qc.invalidateQueries({ queryKey: flowKeys.list() });
    },
  });
}

export const useCreateFlow = () => useFlowWrite((body: FlowCreateBody) => flowsApi.create(body));
export const useSaveFlow = () =>
  useFlowWrite(({ id, body }: { id: string; body: FlowSaveBody }) => flowsApi.save(id, body));
export const usePublishFlow = () => useFlowWrite((id: string) => flowsApi.publish(id));
export const useUnpublishFlow = () => useFlowWrite((id: string) => flowsApi.unpublish(id));

export function useDeleteFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => flowsApi.remove(id),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: flowKeys.one(id) });
      // A entrada da Centuria esquece o fluxo apagado (boas-vindas e atalhos).
      void qc.invalidateQueries({ queryKey: flowKeys.list() });
      void qc.invalidateQueries({ queryKey: flowKeys.settings() });
    },
  });
}

/** Confere um rascunho sem salvar. */
export const useValidateFlow = () =>
  useMutation({ mutationFn: (definition: FlowDocument) => flowsApi.validate(definition) });

/** Roda um passo do teste — nada é gravado. */
export const usePreviewFlow = () =>
  useMutation({ mutationFn: (body: PreviewBody) => flowsApi.preview(body) });

export function useSaveEntrySettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EntrySettingsBody) => flowsApi.saveSettings(body),
    onSuccess: (settings) => {
      qc.setQueryData(flowKeys.settings(), settings);
      // As etiquetas "Boas-vindas" e "Atalho" da lista mudam junto.
      void qc.invalidateQueries({ queryKey: flowKeys.list() });
    },
  });
}

export const useExportResults = () => useMutation({ mutationFn: (id: string) => flowsApi.exportResults(id) });
